import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { colors, breakpoints } from '@/styles'
import { LockTransparent } from '@/components/icons'
type HandleNavLinkClick = (key: string) => void

interface DesktopMenuProps {
  location: { pathname: string | string[] }
  handleNavLinkClick: HandleNavLinkClick
  isLocked: boolean
  isScrolling: boolean
  submittedFilesCount: number
}

const MenuWrapper = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 319px) minmax(350px, 1fr);
  flex: 1;
  max-width: 1205px;
  margin-top: 0px;
  padding-top: 14px;
  font-size: 14px;
  color: ${colors.grey56};

  @media screen and (max-width: ${breakpoints.dashboardMobile}) {
    grid-template-columns: minmax(280px, 305px) minmax(350px, 1fr);
  }

  ::-webkit-scrollbar {
    display: none;
  }

  span {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    padding: 0 10px 14px 10px;
    height: 30px;
    cursor: pointer;

    @media screen and (max-width: ${breakpoints.phoneMax}) {
      display: inline-block;
      justify-content: unset;
      align-items: unset;
      margin: auto 10px;
    }

    &.selected {
      color: ${colors.black};
      border-bottom: 2px solid ${colors.red64};
    }
  }
`

const LeftWrapper = styled.div``

const RightWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`

const MenuItemWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  pointer-events: none;

  svg {
    width: 11px;
  }
`

const Menus = (submittedFilesCount: number) => {
  const { t } = useTranslation()
  return [
    {
      key: 'trust-score',
      label: t('r.liv_score_card.view.current_score.title', 'Trust Score'),
      clickId: 'application-trust-score-sections-score'
    },
    {
      key: 'basic-information',
      label: t('r.profile.edit.basic_information.title', 'Basic Information'),
      clickId: 'application-trust-score-sections-basic-info'
    },
    {
      key: 'references',
      label: t('r.profile.edit.references.tab', 'References'),
      clickId: 'application-trust-score-sections-reference'
    },
    {
      key: 'documents',
      label: t('l.contract.view.verified_documents.title', {
        defaultValue: 'Verified Documents ({{submittedFilesCount}})',
        submittedFilesCount
      }),
      clickId: 'application-trust-score-sections-docs'
    }
  ]
}

export const DesktopMenu: React.FC<DesktopMenuProps> = ({
  location,
  isLocked,
  handleNavLinkClick,
  submittedFilesCount
}) => {
  const menus = Menus(submittedFilesCount)

  const handleNav = useCallback(
    (node: HTMLElement | null) => {
      const selectedItem = node?.querySelector('.selected')
      if (!selectedItem) return

      const selectedItemWidth = selectedItem.getBoundingClientRect().width
      const selectedItemX = selectedItem.getBoundingClientRect().x
      const menuWidth = node?.getBoundingClientRect().width
      const scrollLeft = node?.scrollLeft || 0

      let scrollToX = selectedItemX + selectedItemWidth - menuWidth! + scrollLeft
      if (scrollToX < 0) {
        scrollToX = 0
      }

      node?.scrollTo({
        left: scrollToX,
        behavior: 'smooth'
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location]
  )

  return (
    <MenuWrapper ref={handleNav}>
      <LeftWrapper />
      <RightWrapper>
        {menus.map(({ key, label, clickId }, index) => {
          const current = location.pathname.includes(key)
          const handleClick = () => handleNavLinkClick(key)
          return (
            <span
              key={key}
              id={clickId}
              className={current ? 'selected' : ''}
              onClick={handleClick}
            >
              <MenuItemWrapper>
                {label}
                {index === 0 && isLocked && (
                  <LockTransparent fill={current ? colors.black : colors.grey56} />
                )}
              </MenuItemWrapper>
            </span>
          )
        })}
      </RightWrapper>
    </MenuWrapper>
  )
}
